import { DialogType, EditionBankingInformationDialogProps } from "@interfaces/slices";
import { Box, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { useLazyGetBanksQuery } from "@services/api/banks";
import { NumericFormat, PatternFormat } from "react-number-format";

import * as yup from "yup";
import { UUID } from "crypto";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { setDialogConfig } from "@store/slices/componentsSlice";

import {
    useDeleteBankAccountApplicationMutation,
    useLazyGetBankAccountsApplicationQuery,
    usePatchBankAccountsApplicationMutation,
    usePostBankAccountsApplicationMutation,
} from "@services/api/applications";
import { RootState, store } from "@store/store";
import { useEffect } from "react";
import { usePatchDocumentMutation } from "@services/api/documents";

const MAX_ACCOUNTS_ALLOWED = 5;

interface BankAccountProps {
    id: string | undefined;
    clabe: string;
    bank_card: string;
    bank_id: UUID | string;
    bank_name: string;
    to_delete: boolean;
}

export const EdicionInfoBancaria = (props: EditionBankingInformationDialogProps) => {
    const dispatch = useDispatch();

    const applicationSelected = (state = store.getState()) => state.app.applicationSelected;

    const userSession = useSelector((state: RootState) => state.app.session.user);

    const [triggerGetBanks] = useLazyGetBanksQuery();
    const [triggerPatchDocument] = usePatchDocumentMutation();
    const [triggerGetBankAccount] = useLazyGetBankAccountsApplicationQuery();
    const [triggerPostBankAccount] = usePostBankAccountsApplicationMutation();
    const [triggerPatchBankAccount] = usePatchBankAccountsApplicationMutation();
    const [triggerDeleteBankAccount] = useDeleteBankAccountApplicationMutation();

    const emptyBankAccount: BankAccountProps = {
        id: "0-0-0-0-0",
        clabe: "",
        bank_card: "",
        bank_id: "",
        bank_name: "",
        to_delete: false,
    };

    const formik = useFormik({
        initialValues: {
            accounts: [{ ...emptyBankAccount }],
        },
        validationSchema: yup.object().shape({
            accounts: yup.array().of(
                yup.object().shape({
                    clabe: yup
                        .string()
                        .min(18, "Formato incorrecto, debe tener 18 dígitos")
                        .required("Campo requerido.")
                        .test("unique", "La cuenta bancaria ya se encuentra registrada.", (value) => {
                            const num_results: number = formik.values.accounts.filter((t) => t.clabe === value).length;
                            return num_results > 1 ? false : true;
                        }),
                    bank_card: yup
                        .string()
                        // .min(16, "Formato incorrecto, debe tener 16 dígitos")
                        .notRequired()
                        .test("unique", "El número de tarjeta ya se encuentra registrada", (values) => {
                            const num_results: number = formik.values.accounts.filter(
                                (t) => t.bank_card === values,
                            ).length;
                            return num_results > 1 ? false : true;
                        }),
                    bank_id: yup.string().required("CLABE bancaria incorrecta, validar nuevamente."),
                }),
            ),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                await Promise.all(
                    values.accounts.map(async (account, idx) => {
                        if (account.id === "0-0-0-0-0") {
                            return triggerPostBankAccount({
                                applicationId: applicationSelected(store.getState()).id,
                                payload: {
                                    loan_application_id: applicationSelected(store.getState()).id,
                                    client_details_id:
                                        props.type === "COMPANY"
                                            ? applicationSelected(store.getState()).client_detail_id
                                            : null,
                                    person_id: props.type === "COMPANY" ? null : props.personId,
                                    account: 0,
                                    account_holder: "",
                                    clabe: account.clabe,
                                    bank_card: account.bank_card,
                                    bank_id: account.bank_id as UUID,
                                },
                            });

                            // await formik.setFieldValue(`accounts[${idx}].id`, response.id);
                        } else {
                            return triggerPatchBankAccount({
                                applicationId: applicationSelected(store.getState()).id,
                                bankAccountId: account.id as UUID,
                                payload: {
                                    clabe: account.clabe,
                                    bank_card: account.bank_card,
                                    bank_id: account.bank_id as UUID,
                                },
                            });
                        }
                    }),
                );

                props.onAcceptClick?.();
            } catch (error) {
            } finally {
                setSubmitting(false);
                dispatch(setDialogConfig());
            }
        },
    });

    const handleOnCancelDeleteAccount = async (idx: number) => {
        await formik.setFieldValue(`accounts[${idx}].to_delete`, false);
    };

    const handleOnConfirmDeleteAccount = async (idx: number) => {
        if (formik.values.accounts[idx].id !== "0-0-0-0-0") {
            try {
                formik.setSubmitting(true);
                const accounts = await triggerGetBankAccount(applicationSelected(store.getState()).id).unwrap();

                const account = accounts.find((account) => account.id === formik.values.accounts[idx].id);
                if (account) {
                    await Promise.all(
                        account.documents.map((doc) =>
                            triggerPatchDocument({
                                documentId: doc.document_id,
                                body: {
                                    validated: false,
                                    user_name:
                                        userSession.name
                                            ?.concat(
                                                " ",
                                                userSession?.last_name ?? "",
                                                " ",
                                                userSession?.last_name_2 ?? "",
                                            )
                                            .trim() ?? "",
                                },
                            }),
                        ),
                    );

                    await Promise.all(
                        account.documents.map((doc) =>
                            triggerPatchDocument({
                                documentId: doc.document_id,
                                body: {
                                    status: false,
                                    user_name:
                                        userSession.name
                                            ?.concat(
                                                " ",
                                                userSession?.last_name ?? "",
                                                " ",
                                                userSession?.last_name_2 ?? "",
                                            )
                                            .trim() ?? "",
                                },
                            }),
                        ),
                    );

                    await triggerPatchBankAccount({
                        applicationId: applicationSelected(store.getState()).id,
                        bankAccountId: account.id,
                        payload: {
                            active: false,
                        },
                    }).unwrap();
                }
            } catch (error) {
            } finally {
                formik.setSubmitting(false);
                props.onAcceptClick?.();
                dispatch(setDialogConfig());
            }
        } else {
            const new_accounts = formik.values.accounts.filter((_, index) => index !== idx);
            formik.setFieldValue("accounts", new_accounts);
        }
    };

    const handleOnAddSection = () => {
        if (formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED) return;

        formik.setFieldValue("accounts", [...formik.values.accounts, { ...emptyBankAccount }]);
    };

    const handleOnDeleteSection = async (idx: number) => {
        await formik.setFieldValue(`accounts[${idx}].to_delete`, true);
    };

    const handleOnClabeChanged = async (event: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const { name, value } = event.target;

        await formik.setFieldValue(name, value);

        const bank_code = value.substring(0, 3);
        let searchParams = new URLSearchParams();
        searchParams.append("q", `code:${bank_code}`);
        const bank_info = bank_code.length === 3 ? await triggerGetBanks(searchParams.toString()).unwrap() : null;
        if (bank_info) {
            await formik.setFieldValue(`accounts[${idx}].bank_id`, bank_info[0]?.id || "");
            await formik.setFieldValue(`accounts[${idx}].bank_name`, bank_info[0]?.name || "");
        } else {
            await formik.setFieldValue(`accounts[${idx}].bank_id`, "");
            await formik.setFieldValue(`accounts[${idx}].bank_name`, "");
        }
    };

    const loadForEdition = async () => {
        let searchParams = new URLSearchParams();
        searchParams.append("q", `name:${props.account?.bank_name}`);

        const bank = await triggerGetBanks(searchParams.toString()).unwrap();

        // await formik.setFieldValue(`accounts[0].id`, props.account?.id);
        // await formik.setFieldValue(`accounts[0].clabe`, props.account?.clabe);
        // await formik.setFieldValue(`accounts[0].bank_card`, props.account?.bank_card);
        // await formik.setFieldValue(`accounts[0].bank_name`, props.account?.bank_name);
        // await formik.setFieldValue(`accounts[0].bank_id`, bank[0].id);

        formik.resetForm({
            values: {
                accounts: [
                    {
                        id: props.account?.id,
                        clabe: props.account?.clabe ?? "",
                        bank_card: props.account?.bank_card ?? "",
                        bank_id: bank[0].id,
                        bank_name: props.account?.bank_name ?? "",
                        to_delete: false,
                    },
                ],
            },
        });
    };

    useEffect(() => {
        if (props.account === undefined) return;

        loadForEdition();
    }, [props.account]);

    return (
        <Box margin={4} maxWidth={"400px"} maxHeight={"60vh"}>
            <Stack rowGap={2}>
                <Typography variant="body2" color="main2" fontWeight={600} textAlign={"center"}>
                    Ingresa los datos de la cuenta en dónde se concentra la mayor facturación del negocio, a esta cuenta
                    se depositará el crédito
                </Typography>
                <form onSubmit={formik.handleSubmit} autoComplete="off">
                    {formik.values.accounts.map((_: any, idx: number) => (
                        <Stack rowGap={2} key={idx}>
                            <NumericFormat
                                name={`accounts[${idx}].clabe`}
                                label={"Cuenta CLABE"}
                                required
                                size={"small"}
                                autoComplete="off"
                                displayType="input"
                                customInput={TextField}
                                allowLeadingZeros={true}
                                onBlur={formik.handleBlur}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    handleOnClabeChanged(event, idx)
                                }
                                value={formik.values.accounts[idx].clabe}
                                error={
                                    formik.touched.accounts?.[idx]?.clabe &&
                                    Boolean((formik.errors.accounts as any)?.[idx]?.clabe)
                                }
                                helperText={
                                    formik.touched.accounts?.[idx]?.clabe
                                        ? (formik.errors.accounts as any)?.[idx]?.clabe ||
                                          (formik.errors.accounts as any)?.[idx]?.bank_id
                                        : ""
                                }
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 18,
                                    },
                                }}
                            />

                            <PatternFormat
                                name={`accounts[${idx}].bank_card`}
                                label={"No. de tarjeta"}
                                size={"small"}
                                autoComplete="off"
                                displayType="input"
                                format="#### #### #### ####"
                                mask={"-"}
                                customInput={TextField}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                value={formik.values.accounts[idx].bank_card}
                                error={
                                    formik.touched.accounts?.[idx]?.bank_card &&
                                    Boolean((formik.errors.accounts as any)?.[idx]?.bank_card)
                                }
                                helperText={
                                    formik.touched.accounts?.[idx]?.bank_card
                                        ? (formik.errors.accounts as any)?.[idx]?.bank_card
                                        : ""
                                }
                            />

                            <TextField
                                required
                                size={"small"}
                                name={`accounts[${idx}].bank_name`}
                                label="Banco"
                                disabled
                                autoComplete="off"
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                value={formik.values.accounts[idx].bank_name}
                                error={
                                    formik.touched.accounts?.[idx]?.bank_name &&
                                    Boolean((formik.errors.accounts as any)?.[idx]?.bank_name)
                                }
                                helperText={
                                    formik.touched.accounts?.[idx]?.bank_name
                                        ? (formik.errors.accounts as any)?.[idx]?.bank_name
                                        : ""
                                }
                            />

                            <Box display={"flex"} justifyContent={"end"}>
                                {formik.values.accounts[idx].to_delete === false ? (
                                    <Button
                                        variant="contained"
                                        color="secondary10"
                                        size="small"
                                        onClick={() => handleOnDeleteSection(idx)}
                                        disabled={formik.values.accounts.length === 1 && props.account === undefined}
                                    >
                                        -
                                    </Button>
                                ) : (
                                    <Box display={"flex"} columnGap={2}>
                                        <Button
                                            variant="contained"
                                            color="secondary4"
                                            onClick={() => handleOnCancelDeleteAccount(idx)}
                                            loading={formik.isSubmitting}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            onClick={() => handleOnConfirmDeleteAccount(idx)}
                                            loading={formik.isSubmitting}
                                        >
                                            Confirmar eliminado
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                            <Divider />
                        </Stack>
                    ))}
                    <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        columnGap={3}
                        display={props.account === undefined ? "flex" : "none"}
                    >
                        <Typography component={"span"} variant="body2" color="main3" fontWeight={500}>
                            Nota:{" "}
                            <Typography component={"span"} variant="body2" color="main3" fontWeight={300}>
                                Si la cuenta refleja menos del 50% de los ingresos agrega otra cuenta bancaria hasta
                                llegar este porcentaje.
                            </Typography>
                        </Typography>

                        <Button
                            variant="contained"
                            color="secondary4"
                            sx={{ height: "30px", alignSelf: "center" }}
                            onClick={handleOnAddSection}
                            disabled={formik.values.accounts.length === MAX_ACCOUNTS_ALLOWED}
                        >
                            +
                        </Button>
                    </Stack>
                    <Stack direction={"row"} justifyContent={"space-around"} mt={2}>
                        <Button
                            variant="blue_outlined"
                            disabled={formik.isSubmitting}
                            onClick={() => dispatch(setDialogConfig())}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="main2"
                            type="submit"
                            disabled={!formik.dirty || formik.isSubmitting}
                            loading={formik.isSubmitting}
                        >
                            Guardar
                        </Button>
                    </Stack>
                </form>
            </Stack>
        </Box>
    );
};
